// WHAT ONE PARTNER PERSON CAN REACH, AND WHY (owner, 2026-10-05: "a button
// on each user to show a popup with all the permissions they have split
// between, magic links, account, roaster account, member of an org ... and
// allow the user interface to add and remove permissions, like a checkbox,
// check bidlight users and permissions page").
//
// THE FOUR SOURCES GET FOUR SECTIONS, in the order somebody reads them:
//
//   ACCOUNT        which kind it is. This is the answer to "who is this?"
//                  and it decides whether anything below can be changed.
//   ON THE ROSTER  which projects, as what. Being listed is what makes
//                  somebody a partner at all; it grants nothing by itself
//                  and it decides the tier that does.
//   PERMISSIONS    the checkboxes, grouped as the company permissions page
//                  groups them, with the tier's own grants marked so a
//                  reader can tell a default from a decision.
//   LINKS          what reaches a page with no permission at all. The
//                  section most likely to surprise somebody auditing
//                  access, which is why it is here rather than nowhere.
//
// WHY A TICKED BOX CAN STILL MEAN NO ACCESS, said on the box itself: the
// app renders the INTERSECTION of the person's grants and their
// organisation's, so a grant their company does not hold does nothing. The
// server reports those as `blockedByOrg`, and this draws them struck
// through with the reason - because the alternative is a dialog that shows
// a tick for access the person does not have.
//
// THE COORDINATION GROUP (scope "partnerRow", 2026-10-15) IS NOT CAPPED BY
// THEIR ORGANISATION. Those keys are kept on your partner rows, not in the
// person's own permission list, and add to what their company's Coordination
// access switches already give them on your projects - so they are never
// drawn "no effect" and never tagged "tier".
//
// ONE DIALOG, TWO SCOPES (owner, 2026-10-16: merge the separate Access
// editors into one). The Partners view used to have two: this one, for a
// person across all of your projects, and a smaller one on the project
// roster. They edit the same thing - the person's account grants - so they
// are one dialog now, and only WHERE it reads and saves differs:
//
//   scope omitted       /partner-directory/people/access + /permissions
//                       (company admins; the four sections; add/remove pair)
//   scope = { projectId, rowId }
//                       /projects/:id/participants/:row/access
//                       (whoever may edit this project's roster; saving
//                       creates the account if there is none, emailing
//                       nobody; sends the ticked partner-roster keys)
//
// Both answers are turned into one shape (`fromDirectory` / `fromRoster`), so
// every box, tag, "Standard partner access", "Clear all", "Undo changes" and
// the +N -M Save label behave the same in both places.
//
// NOTHING IS SAVED AS IT IS CLICKED. The boxes collect a change and Save
// sends one add/remove pair, so a half-finished set of clicks is never a
// half-applied grant.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, Empty, Modal, Skeleton, Space, Tag, Tooltip, message } from "antd";
import { getPartnerAccess, setPartnerPermissions } from "api/partners/partnerDirectoryApi";
import { getRosterAccess, setRosterAccess } from "api/participants/participantsApi";

const T = { muted: "#667085", line: "#EFEFEF" };

// camelCase -> words, the way the company permissions page prints them, so
// the two pages name the same grant the same way.
export const permLabel = (k) => String(k || "")
  .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
  .replace(/^3d/, "3D")
  .replace(/^./, (c) => c.toUpperCase());

const KIND_TAG = {
  partner: { color: "blue", label: "Partner account" },
  member: { color: "green", label: "Company member" },
  companyAdmin: { color: "gold", label: "Company admin" },
  superAdmin: { color: "red", label: "Super admin" },
  none: { color: undefined, label: "No account" },
};

/** The Account column's cell: WHICH KIND of access this person has.
 *
 * Shown from the directory row alone - no request per row - because the
 * column has to draw for a hundred people at once. The directory knows
 * whether an account exists and whether they were invited; the KIND it is
 * comes from the dialog, which is one request for the one person being
 * looked at.
 */
export function AccountCell({ person }) {
  if (!person.person && !person.account) {
    // Listed only as a supervisor or executive contact: a person on the
    // roster, not a roster USER row.
    return <Tooltip title="A named contact on the roster - no account, reached by email and by share links"><Tag>Contact only</Tag></Tooltip>;
  }
  if (person.account) {
    return (
      <Tooltip title="Has an account and signs in. Open Access to see which kind and what it can do.">
        <Tag color="blue">Has account</Tag>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={person.invitedOn
      ? `Invited on ${person.invitedOn} project${person.invitedOn === 1 ? "" : "s"} - no account yet, so only share links reach them`
      : "On the roster with no account and no invitation - only share links reach them"}>
      {/* THE COUNT STAYS IN THE CELL. It was there before this column
          started naming the kind of access, and an antd Tooltip renders
          its text on hover rather than as a title attribute - so moving
          the number into the hover would have removed it from the page
          and from anything that can check it. */}
      <Tag>{person.invitedOn ? `Invited (${person.invitedOn})` : "Roster contact"}</Tag>
    </Tooltip>
  );
}

export const fromDirectory = (d) => ({
  scope: "all",
  email: d.email,
  editable: !!d.editable,
  notEditableWhy: d.notEditableWhy || "",
  accountKind: d.accountKind,
  accountLabel: d.accountLabel,
  tierLabel: d.tierLabel || "",
  account: d.account,
  memberships: d.memberships || [],
  links: d.links || null,
  groups: (d.grantable || []).map((g) => ({ title: g.group, keys: g.keys, scope: g.scope })),
  labels: d.labels || {},
  standard: d.standard || [],
  held: (d.permissions && d.permissions.held) || [],
  tierDefault: (d.permissions && d.permissions.tierDefault) || [],
  blocked: (d.permissions && d.permissions.blockedByOrg) || [],
  ceiling: d.organisation ? d.organisation.ceiling : null,
  ceilingKnown: true,
  createsAccount: false,
});
export const fromRoster = (r, { companyName = "" } = {}) => ({
  scope: "project",
  email: r.email,
  editable: !r.fullAccount,
  notEditableWhy: r.fullAccount ? "This is an administrator account of their company - its access is managed by their company, not here." : "",
  accountKind: !r.hasAccount ? "none" : (r.fullAccount ? "companyAdmin" : "partner"),
  accountLabel: !r.hasAccount ? "No account yet" : (r.fullAccount ? "Company admin" : "Has account"),
  tierLabel: "",
  account: r.hasAccount ? {} : null,
  companyName,
  memberships: null,
  links: null,
  groups: (r.groups || []).map((g) => ({ title: g.title, keys: g.keys })),
  labels: r.labels || {},
  standard: r.standard || [],
  held: r.keys || [],
  tierDefault: [],
  blocked: [],
  ceiling: null,
  ceilingKnown: false,
  createsAccount: !r.hasAccount,
});

export default function PartnerAccessDialog({ email, name, scope = null, onClose }) {
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const [want, setWant] = useState(null);        // the set of keys as the boxes stand
  const [saving, setSaving] = useState(false);
  const projectId = scope && scope.projectId;
  const rowId = scope && scope.rowId;
  const companyName = (scope && scope.companyName) || "";

  const load = useCallback(() => {
    if (!email) return;
    setState({ loading: true, data: null, error: "" });
    const read = projectId
      ? getRosterAccess(projectId, rowId, email).then((r) => fromRoster(r, { companyName }))
      : getPartnerAccess(email).then(fromDirectory);
    read
      .then((d) => {
        setState({ loading: false, data: d, error: "" });
        setWant(new Set(d.held));
      })
      .catch((e) => setState({ loading: false, data: null, error: e.message || "Could not read that person's access" }));
  }, [email, projectId, rowId, companyName]);
  useEffect(() => { load(); }, [load]);

  const d = state.data;
  const held = useMemo(() => new Set((d && d.held) || []), [d]);
  const tierDefault = useMemo(() => new Set((d && d.tierDefault) || []), [d]);
  const blocked = useMemo(() => new Set((d && d.blocked) || []), [d]);
  const ceiling = d ? d.ceiling : null;
  const grantableKeys = useMemo(() => new Set(((d && d.groups) || []).flatMap((g) => g.keys)), [d]);
  const labelOf = (k) => (d && d.labels && d.labels[k]) || permLabel(k);

  // What Save would send. Computed from the two sets rather than collected
  // as clicks happen: clicking a box twice is no change, and a diff says
  // that where a list of events cannot.
  const diff = useMemo(() => {
    if (!want) return { add: [], remove: [] };
    return {
      add: [...want].filter((k) => !held.has(k)),
      remove: [...held].filter((k) => !want.has(k)),
    };
  }, [want, held]);
  // A person with no account yet is created by Save on a project roster, so
  // Save is offered even with nothing ticked differently.
  const dirty = diff.add.length > 0 || diff.remove.length > 0;

  const save = async () => {
    setSaving(true);
    try {
      if (d.scope === "project") {
        // The roster route takes the whole partner-roster set, so it is sent
        // as the boxes stand - only keys this dialog lists.
        const keys = [...want].filter((k) => grantableKeys.has(k));
        await setRosterAccess(projectId, rowId, email, keys);
        const bits = [];
        if (diff.add.length) bits.push(`${diff.add.length} added`);
        if (diff.remove.length) bits.push(`${diff.remove.length} removed`);
        message.success(`Access saved for ${name || email}${bits.length ? ` - ${bits.join(", ")}` : ""}`);
      } else {
        const r = await setPartnerPermissions(email, diff);
        const bits = [];
        if ((r.added || []).length) bits.push(`${r.added.length} added`);
        if ((r.removed || []).length) bits.push(`${r.removed.length} removed`);
        message.success(bits.length ? bits.join(", ") : "Nothing changed");
        // GRANTED AND INERT IS NOT A SILENT SUCCESS: the person's own
        // organisation does not allow it, so it does nothing until that
        // changes, and nobody would discover that from a green toast.
        if ((r.inert || []).length) {
          message.warning(`${r.inert.map(labelOf).join(", ")} ${r.inert.length === 1 ? "is" : "are"} granted but does nothing yet - their organisation's own permissions do not include it.`);
        }
      }
      load();
    } catch (e) {
      message.error(e.message || "Could not change those permissions");
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key, on) => setWant((prev) => {
    const next = new Set(prev || []);
    if (on) next.add(key); else next.delete(key);
    return next;
  });
  // Both act on the keys this dialog lists only: a grant made elsewhere is
  // never added or removed by a shortcut button here.
  const addStandard = () => setWant((prev) => new Set([...(prev || []), ...((d && d.standard) || []).filter((k) => grantableKeys.has(k))]));
  const clearAll = () => setWant((prev) => new Set([...(prev || [])].filter((k) => !grantableKeys.has(k))));
  const canSave = d && d.editable && (dirty || d.createsAccount);

  return (
    <Modal
      open={!!email}
      title={`Access for ${name || email}`}
      onCancel={onClose}
      width={760}
      footer={[
        <Button key="c" onClick={onClose}>Close</Button>,
        ...(d && d.editable ? [
          <Button key="r" disabled={!dirty || saving} onClick={() => setWant(new Set(held))}>Undo changes</Button>,
          <Button key="s" type="primary" loading={saving} disabled={!canSave} onClick={save}>
            {dirty ? `Save ${diff.add.length ? `+${diff.add.length}` : ""}${diff.add.length && diff.remove.length ? " " : ""}${diff.remove.length ? `-${diff.remove.length}` : ""}` : "Save"}
          </Button>,
        ] : []),
      ]}
    >
      {state.loading ? <Skeleton active paragraph={{ rows: 6 }} /> : null}
      {state.error ? <Alert type="error" showIcon message={state.error} /> : null}
      {d ? (
        <div>
          {/* ---- ACCOUNT ---- */}
          <Section title="Account">
            <Space wrap>
              <Tag color={(KIND_TAG[d.accountKind] || {}).color}>{d.accountLabel}</Tag>
              {d.tierLabel ? (
                <Tooltip title="Set by the roster: a supervisor may raise issues, an employee may only update them, an executive reads.">
                  <Tag color="purple">{d.tierLabel}</Tag>
                </Tooltip>
              ) : null}
              <span style={{ color: T.muted }}>{d.email}{d.companyName ? ` - ${d.companyName}` : ""}</span>
            </Space>
            <div style={{ color: T.muted, fontSize: 12, marginTop: 6 }}>
              This is their BidLight account&apos;s access, so it applies on every project they are on.
              {d.createsAccount ? " They have no account yet: saving creates it (no email is sent)." : ""}
            </div>
            {!d.editable && d.notEditableWhy ? (
              <div style={{ marginTop: 8 }}>
                <Alert type="info" showIcon message={d.notEditableWhy} />
              </div>
            ) : null}
          </Section>

          {/* ---- ON THE ROSTER (across all projects only) ---- */}
          {d.memberships ? (
            <Section title="On the roster">
              {d.memberships.length ? (
                <div>
                  {d.memberships.map((m) => (
                    <div key={m.projectId} style={{ marginBottom: 4 }}>
                      <b>{m.projectTitle || m.projectId}</b>
                      <span style={{ color: T.muted }}>
                        {" · "}{m.companyName || "their company"}
                        {m.title ? ` · ${m.title}` : ""}
                        {m.trades && m.trades.length ? ` · ${m.trades.join(", ")}` : ""}
                      </span>
                      {m.supervisor ? <Tag color="blue" style={{ marginLeft: 6 }}>supervisor</Tag> : null}
                      {m.executive ? <Tag color="purple" style={{ marginLeft: 6 }}>executive</Tag> : null}
                      {!m.onRoster ? (
                        <Tooltip title="Named as a contact on this project but not in its roster list">
                          <Tag style={{ marginLeft: 6 }}>contact only</Tag>
                        </Tooltip>
                      ) : null}
                    </div>
                  ))}
                  <div style={{ color: T.muted, fontSize: 12, marginTop: 6 }}>
                    Being on a roster grants nothing by itself - it is what scopes everything below to these projects,
                    and what decides the tier.
                  </div>
                </div>
              ) : <Empty image={null} description="Not on any of your projects" />}
            </Section>
          ) : null}

          {/* ---- PERMISSIONS ---- */}
          <Section title="Permissions">
            {d.ceilingKnown && ceiling === null ? (
              <div style={{ color: T.muted, fontSize: 12, marginBottom: 8 }}>
                Their organisation has no permission list of its own, so nothing here is capped by it.
              </div>
            ) : null}
            {d.editable && ((d.standard || []).some((k) => grantableKeys.has(k)) || grantableKeys.size) ? (
              <div style={{ marginBottom: 8 }}>
                {(d.standard || []).some((k) => grantableKeys.has(k)) ? (
                  <Button size="small" onClick={addStandard}>Standard partner access</Button>
                ) : null}
                <Button size="small" type="link" onClick={clearAll}>Clear all</Button>
              </div>
            ) : null}
            {(d.groups || []).map((g) => (
              <div key={g.title} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: T.muted, textTransform: "uppercase", letterSpacing: 0.4 }}>{g.title}</div>
                {g.scope === "partnerRow" ? (
                  <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
                    For this person only, on every one of your projects they are on - on top of what their company&apos;s
                    Coordination access already allows. Their own organisation&apos;s permissions do not limit these.
                  </div>
                ) : null}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginTop: 4 }}>
                  {g.keys.map((k) => {
                    const on = !!(want && want.has(k));
                    const rowScoped = g.scope === "partnerRow";
                    const inert = !rowScoped && (blocked.has(k) || (ceiling !== null && !ceiling.includes(k)));
                    return (
                      <Checkbox
                        key={k}
                        value={k}
                        checked={on}
                        disabled={!d.editable}
                        onChange={(e) => toggle(k, e.target.checked)}
                      >
                        <span style={inert && on ? { textDecoration: "line-through" } : undefined}>{labelOf(k)}</span>
                        {!rowScoped && tierDefault.has(k) ? (
                          <Tooltip title="Part of what this tier grants by default - it comes back on the next roster change if you remove it">
                            <Tag style={{ marginLeft: 6 }}>tier</Tag>
                          </Tooltip>
                        ) : null}
                        {inert ? (
                          <Tooltip title="Their organisation's own permissions do not include this, and the app shows only what both allow - so it does nothing until that changes">
                            <Tag color="orange" style={{ marginLeft: 6 }}>no effect</Tag>
                          </Tooltip>
                        ) : null}
                      </Checkbox>
                    );
                  })}
                </div>
              </div>
            ))}
            {/* HELD BUT NOT LISTED ABOVE: a grant made elsewhere that this
                page may not change. Shown rather than hidden - a dialog
                claiming to list "all the permissions they have" has to. */}
            {(() => {
              const others = (d.held || []).filter((k) => !grantableKeys.has(k));
              if (!others.length) return null;
              return (
                <div style={{ borderTop: `1px solid ${T.line}`, paddingTop: 8, marginTop: 4 }}>
                  <div style={{ fontSize: 12, color: T.muted, textTransform: "uppercase", letterSpacing: 0.4 }}>
                    Also held, and not changed from here
                  </div>
                  <Space wrap style={{ marginTop: 4 }}>
                    {others.map((k) => <Tag key={k}>{labelOf(k)}</Tag>)}
                  </Space>
                  <div style={{ color: T.muted, fontSize: 12, marginTop: 4 }}>
                    Granted somewhere other than this page. It is shown so this list is the whole truth, and left alone
                    because this page only changes the partner-side keys above.
                  </div>
                </div>
              );
            })()}
          </Section>

          {/* ---- LINKS (across all projects only) ---- */}
          {d.links ? (
            <Section title="Links">
              <div style={{ marginBottom: 6 }}>
                <Tag color={d.links.magic.eligible ? "green" : undefined}>
                  {d.links.magic.eligible ? "Signed-in links work" : "No signed-in links"}
                </Tag>
                <span style={{ color: T.muted }}>{d.links.magic.why}</span>
              </div>
              {(d.links.shares || []).length ? (
                <div>
                  {d.links.shares.map((s, i) => (
                    <div key={`${s.resourceType}-${s.resourceId}-${i}`} style={{ fontSize: 12.5 }}>
                      <Tag>{s.kind === "guest" ? "Guest link" : s.kind}</Tag>
                      {s.resourceType}
                      <span style={{ color: T.muted }}>
                        {s.opened ? ` · opened ${s.opened} time${s.opened === 1 ? "" : "s"}` : " · never opened"}
                        {s.expired ? " · expired" : ""}
                      </span>
                    </div>
                  ))}
                  <div style={{ color: T.muted, fontSize: 12, marginTop: 6 }}>
                    A guest link reaches one page without an account and without any permission above - revoke it from
                    that item&apos;s share dialog.
                  </div>
                </div>
              ) : <div style={{ color: T.muted, fontSize: 12.5 }}>No guest links.</div>}
            </Section>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ borderTop: `1px solid ${T.line}`, paddingTop: 10, marginTop: 10 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}
